import React, { useState, useEffect, useCallback, useRef } from 'react';
import NewsFeedTile from './NewsFeedTile';
import '../styles/NewsFeed.css';

const NewsFeed = ({ apiBaseUrl, filters = {}, limit = null }) => {
  const [updates, setUpdates] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const observerRef = useRef(null);
  const sentinelRef = useRef(null);
  const isFetching = useRef(false);
  const prevFilters = useRef(filters);

  const fetchUpdates = useCallback(
    async (pageNum) => {
      if (isFetching.current || !hasMore) {
        console.log(`Skipping fetch: page=${pageNum}, fetching=${isFetching.current}, hasMore=${hasMore}`);
        return;
      }
      isFetching.current = true;
      setLoading(true);
      console.log(`Fetching newsfeed, page: ${pageNum}, filters:`, filters);
      try {
        const params = new URLSearchParams({
          page: pageNum,
          per_page: limit || 25,
          ...filters,
        });
        const response = await fetch(`${apiBaseUrl}/newsfeed?${params}`, {
          method: 'GET',
          credentials: 'include',
        });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        console.log('Fetched data:', data);
        const { updates: newUpdates, pages } = data;
        setUpdates(prev => (pageNum === 1 ? newUpdates : [...prev, ...newUpdates]));
        setHasMore(pageNum < pages);
      } catch (error) {
        console.error('Error fetching newsfeed:', error);
      } finally {
        setLoading(false);
        isFetching.current = false;
      }
    },
    [apiBaseUrl, filters, limit, hasMore]
  );

  useEffect(() => {
    const filtersChanged = JSON.stringify(prevFilters.current) !== JSON.stringify(filters);
    console.log('Filters check:', { prev: prevFilters.current, current: filters, changed: filtersChanged });

    if (!filtersChanged) {
      console.log('Skipping feed reset: filters unchanged');
      return;
    }

    console.log('Resetting feed due to filter change:', filters);
    prevFilters.current = filters;
    setPage(0);
    setUpdates([]);
    setHasMore(true);
    fetchUpdates(1);
  }, [filters, fetchUpdates]);

  const observerCallback = useCallback(
    (entries) => {
      if (entries[0].isIntersecting && hasMore && !isFetching.current) {
        console.log('Sentinel visible, loading page:', page + 1);
        setPage(prev => {
          const nextPage = prev + 1;
          fetchUpdates(nextPage);
          return nextPage;
        });
      } else {
        console.log('Sentinel not triggered:', {
          isIntersecting: entries[0].isIntersecting,
          hasMore,
          isFetching: isFetching.current,
        });
      }
    },
    [hasMore, page, fetchUpdates]
  );

  useEffect(() => {
    if (!sentinelRef.current || !hasMore || loading) {
      console.log('Skipping observer setup:', { hasMore, loading });
      return;
    }

    console.log('Setting up IntersectionObserver for page:', page);
    observerRef.current = new IntersectionObserver(observerCallback, {
      threshold: 0, // Trigger as soon as sentinel enters viewport
      rootMargin: '300px', // Trigger 300px before sentinel
    });

    observerRef.current.observe(sentinelRef.current);

    return () => {
      if (observerRef.current && sentinelRef.current) {
        observerRef.current.unobserve(sentinelRef.current);
        console.log('Cleaned up observer for page:', page);
      }
    };
  }, [observerCallback, hasMore, loading, page]);

  return (
    <div className="newsfeed">
      {updates.length === 0 && !loading && <p>No updates found.</p>}
      <div className="newsfeed-grid">
        {updates.map(update => (
          <NewsFeedTile key={update.id} update={update} apiBaseUrl={apiBaseUrl}/>
        ))}
      </div>
      {loading && <p>Loading...</p>}
      <div ref={sentinelRef} style={{ height: '20px', background: 'transparent' }} />
    </div>
  );
};

export default NewsFeed;